import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Reorder } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_META, CATEGORY_ORDER, STATUS_META, STATUS_ORDER } from "@/lib/constants";
import { Plus, ChevronLeft, Trash2, MoreVertical, FolderInput, GripVertical, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const money = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const HEALTH = { red: "bg-rose-500", amber: "bg-amber-500", green: "bg-emerald-500" };

function ProjectRow({ p, canManage, isAdmin, isUngrouped, category, builders, builderId, navigate, moveToBuilder, moveToCategory, remove }) {
  const statusPills = useMemo(
    () => STATUS_ORDER.filter((s) => p.task_counts?.[s] > 0),
    [p.task_counts]
  );
  const targetBuilders = useMemo(
    () => builders.filter((b) => b.builder_id !== builderId),
    [builders, builderId]
  );
  const targetCategories = useMemo(
    () => CATEGORY_ORDER.filter((c) => c !== category),
    [category]
  );
  return (
    <>
      {canManage && <GripVertical className="h-4 w-4 text-zinc-300 cursor-grab active:cursor-grabbing shrink-0" />}
      {p.task_total > 0 && (
        <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${HEALTH[p.health] || "bg-zinc-300"}`}
          title={p.health === "red" ? "Has overdue tasks" : p.health === "amber" ? "Due soon / checklist incomplete" : "On track"}
          data-testid={`project-health-${p.project_id}`} />
      )}
      <button className="flex-1 min-w-0 text-left" onClick={() => navigate(`/project/${p.project_id}`)} data-testid={`project-card-${p.project_id}`}>
        <h3 className="font-heading font-bold text-base leading-snug truncate">{p.name}</h3>
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {statusPills.map((s) => (
            <span key={s} className="text-[10px] font-semibold px-2 py-0.5 rounded-sm"
              style={{ background: STATUS_META[s].bg, color: STATUS_META[s].color }}>
              {p.task_counts[s]} {STATUS_META[s].label}
            </span>
          ))}
          {p.task_total === 0 && <span className="text-xs text-zinc-400">No tasks yet</span>}
        </div>
        {p.consultant_overdue_count > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-sm bg-rose-100 text-rose-700 border border-rose-200 mt-2"
            data-testid={`project-consultant-overdue-${p.project_id}`}>
            <AlertTriangle className="h-3 w-3" /> {p.consultant_overdue_count} consultant{p.consultant_overdue_count !== 1 ? "s" : ""} overdue
          </span>
        )}
        {isAdmin && p.outstanding_fees > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200 mt-2"
            data-testid={`project-outstanding-${p.project_id}`}>
            Outstanding {money.format(p.outstanding_fees)}
          </span>
        )}
        {p.checklist_total > 0 && (
          <div className="flex items-center gap-2 mt-2 max-w-xs" data-testid={`project-progress-${p.project_id}`}>
            <div className="h-1.5 flex-1 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${Math.round((p.checklist_done / p.checklist_total) * 100)}%` }} />
            </div>
            <span className="text-[10px] font-semibold text-zinc-500">{p.checklist_done}/{p.checklist_total}</span>
          </div>
        )}
        {p.doc_checklist?.enabled && (
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-sm mt-2 ${p.doc_checklist.missing > 0 ? "bg-rose-100 text-rose-700 border border-rose-200" : "bg-emerald-100 text-emerald-700 border border-emerald-200"}`}
            data-testid={`project-docs-${p.project_id}`}>
            <FileText className="h-3 w-3" />
            {p.doc_checklist.received}/{p.doc_checklist.total} documents{p.doc_checklist.missing > 0 ? ` · ${p.doc_checklist.missing} missing` : " · complete"}
          </span>
        )}
      </button>
      {canManage && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="text-zinc-400 hover:text-zinc-900 transition-colors shrink-0" data-testid={`project-menu-${p.project_id}`}>
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-sm w-56">
            <DropdownMenuLabel className="flex items-center gap-1.5 text-xs"><FolderInput className="h-3.5 w-3.5" /> Move</DropdownMenuLabel>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>To builder folder</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="rounded-sm">
                {targetBuilders.map((b) => (
                  <DropdownMenuItem key={b.builder_id} onClick={() => moveToBuilder(p.project_id, b.builder_id)} data-testid={`move-builder-${p.project_id}-${b.builder_id}`}>
                    {b.name}
                  </DropdownMenuItem>
                ))}
                {!isUngrouped && <DropdownMenuItem onClick={() => moveToBuilder(p.project_id, null)}>Ungrouped</DropdownMenuItem>}
                {builders.length === 0 && <DropdownMenuItem disabled>No other folders</DropdownMenuItem>}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>To another category</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="rounded-sm">
                {targetCategories.map((c) => (
                  <DropdownMenuItem key={c} onClick={() => moveToCategory(p.project_id, c)} data-testid={`move-cat-${p.project_id}-${c}`}>
                    {CATEGORY_META[c].label} · {CATEGORY_META[c].sub}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm text-red-600 hover:bg-red-50" data-testid={`delete-project-${p.project_id}`}>
                  <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete project
                </div>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-sm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this project?</AlertDialogTitle>
                  <AlertDialogDescription>This permanently removes &ldquo;{p.name}&rdquo; and all its tasks.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                  <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => remove(p.project_id)}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}

export default function BuilderView() {
  const { category, builderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.can_manage;
  const isAdmin = user?.role === "admin";
  const meta = CATEGORY_META[category];
  const isUngrouped = builderId === "none";
  const isInterior = category?.startsWith("interior");

  const [builder, setBuilder] = useState(null);
  const [projects, setProjects] = useState([]);
  const [builders, setBuilders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", apply_template: false, enable_checklist: false });
  const [saving, setSaving] = useState(false);
  const [builderEdit, setBuilderEdit] = useState(false);
  const [builderForm, setBuilderForm] = useState({ contact_person: "", email: "", phone: "" });
  const [logoBusy, setLogoBusy] = useState(false);
  const logoInput = useRef(null);
  const projectsRef = useRef([]);
  useEffect(() => { projectsRef.current = projects; }, [projects]);

  const load = useCallback(async () => {
    setLoading(true);
    const reqs = [
      api.get("/projects", { params: { category, builder_id: builderId } }),
      api.get("/builders", { params: { category } }),
    ];
    if (!isUngrouped) reqs.push(api.get(`/builders/${builderId}`));
    const [p, b, bd] = await Promise.all(reqs);
    setProjects(p.data);
    setBuilders(b.data);
    setBuilder(bd ? bd.data : null);
    setLoading(false);
  }, [category, builderId, isUngrouped]);
  useEffect(() => { load(); }, [load]);

  const persistOrder = async () => {
    try { await api.post("/projects/reorder", { ordered_ids: projectsRef.current.map((p) => p.project_id) }); }
    catch (e) { toast.error("Failed to save order"); }
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Project name is required"); return; }
    setSaving(true);
    try {
      await api.post("/projects", {
        name: form.name, category, description: form.description,
        builder_id: isUngrouped ? null : builderId, apply_template: form.apply_template,
        enable_checklist: form.enable_checklist,
      });
      toast.success("Project created");
      setForm({ name: "", description: "", apply_template: false, enable_checklist: false });
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e, "Failed to create project")); }
    finally { setSaving(false); }
  };

  const saveBuilder = async () => {
    try {
      await api.patch(`/builders/${builderId}`, { contact_person: builderForm.contact_person, email: builderForm.email, phone: builderForm.phone });
      setBuilderEdit(false); toast.success("Builder details updated"); load();
    } catch (e) { toast.error(apiErr(e, "Failed to update builder")); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/builders/${builderId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated"); load();
    } catch (e) { toast.error(apiErr(e, "Logo upload failed")); }
    finally { setLogoBusy(false); }
  };

  const moveToBuilder = async (id, targetBuilderId) => {
    try { await api.patch(`/projects/${id}`, { builder_id: targetBuilderId || "none" }); toast.success("Project moved"); load(); }
    catch (e) { toast.error("Failed to move"); }
  };
  const moveToCategory = async (id, cat) => {
    try { await api.patch(`/projects/${id}`, { category: cat }); toast.success("Project moved to another folder"); load(); }
    catch (e) { toast.error("Failed to move"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/projects/${id}`); toast.success("Project deleted"); load(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  if (!meta) return <div className="p-10">Unknown folder</div>;
  const title = isUngrouped ? "Ungrouped" : (builder?.name || "…");

  const rowProps = { canManage, isAdmin, isUngrouped, category, builders, builderId, navigate, moveToBuilder, moveToCategory, remove };

  return (
    <div className="p-6 lg:p-10 max-w-[900px]">
      <button onClick={() => navigate(`/folder/${category}`)}
        className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 transition-colors mb-4" data-testid="back-to-folder">
        <ChevronLeft className="h-4 w-4" /> {meta.label} · {meta.sub}
      </button>

      <header className="flex items-start justify-between mb-8 gap-4">
        <div>
          <h1 className="font-heading text-4xl font-black tracking-tighter">{title}</h1>
          <p className="text-zinc-500 mt-2 text-sm">{projects.length} project{projects.length !== 1 ? "s" : ""}{canManage && projects.length > 1 ? " · drag to reorder" : ""}</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-button" className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-2">
                <Plus className="h-4 w-4" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle className="font-heading">New Project</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Project Name</label>
                  <Input data-testid="project-name-input" value={form.name} className="mt-1.5 rounded-sm"
                    placeholder="e.g. 74 Darcy Road Wentworthville"
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  {!isUngrouped && <p className="text-[11px] text-zinc-400 mt-1">Will be added to <span className="font-medium">{title}</span>.</p>}
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Description</label>
                  <Textarea value={form.description} className="mt-1.5 rounded-sm"
                    placeholder="Optional notes" onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="flex items-center justify-between border border-zinc-200 rounded-sm px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">Apply {isInterior ? "Interior" : "Architecture"} Template</p>
                    <p className="text-xs text-zinc-500">Pre-fill the standard {isInterior ? "interior" : "architecture"} task checklist</p>
                  </div>
                  <Switch data-testid="apply-template-switch" checked={form.apply_template}
                    onCheckedChange={(v) => setForm({ ...form, apply_template: v })} />
                </div>
                <div className="flex items-center justify-between border border-zinc-200 rounded-sm px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">Enable Document Checklist</p>
                    <p className="text-xs text-zinc-500">Track pre-design documents (contract, DP, S88B…)</p>
                  </div>
                  <Switch data-testid="enable-checklist-switch" checked={form.enable_checklist}
                    onCheckedChange={(v) => setForm({ ...form, enable_checklist: v })} />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-project-button" onClick={create} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm">
                  {saving ? "Creating…" : "Create Project"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </header>

      {!isUngrouped && builder && (
        <div className="border border-zinc-200 bg-white rounded-sm p-4 mb-8 flex items-center gap-4" data-testid="builder-details">
          <div className="shrink-0">
            {builder.logo_path ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${builder.logo_path}`} alt="logo"
                className="h-14 w-14 rounded-sm object-contain border border-zinc-100 bg-white" data-testid="builder-logo" />
            ) : (
              <div className="h-14 w-14 rounded-sm bg-zinc-100 flex items-center justify-center text-zinc-400 font-heading font-black text-xl">
                {title.charAt(0)}
              </div>
            )}
          </div>
          {builderEdit ? (
            <div className="flex-1 grid sm:grid-cols-2 gap-2">
              <Input value={builderForm.contact_person} onChange={(e) => setBuilderForm({ ...builderForm, contact_person: e.target.value })}
                placeholder="Contact person" className="rounded-sm h-9 text-sm" data-testid="builder-contact-input" />
              <Input value={builderForm.email} onChange={(e) => setBuilderForm({ ...builderForm, email: e.target.value })}
                placeholder="Builder email" className="rounded-sm h-9 text-sm" data-testid="builder-email-input" />
              <Input value={builderForm.phone} onChange={(e) => setBuilderForm({ ...builderForm, phone: e.target.value })}
                placeholder="Phone" className="rounded-sm h-9 text-sm" data-testid="builder-phone-input" />
              <div className="flex gap-2 sm:col-span-2">
                <Button size="sm" onClick={saveBuilder} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm h-8" data-testid="save-builder">Save</Button>
                <Button size="sm" variant="outline" onClick={() => setBuilderEdit(false)} className="rounded-sm h-8">Cancel</Button>
                <input ref={logoInput} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
                  onChange={(e) => { uploadLogo(e.target.files[0]); e.target.value = ""; }} data-testid="logo-input" />
                <Button size="sm" variant="outline" onClick={() => logoInput.current?.click()} disabled={logoBusy} className="rounded-sm h-8 gap-1" data-testid="upload-logo">
                  {logoBusy ? "Uploading…" : "Upload logo"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Builder</p>
              <div className="flex flex-wrap gap-x-5 gap-y-0.5 text-sm mt-0.5">
                <span data-testid="builder-contact">{builder.contact_person ? builder.contact_person : <span className="text-zinc-400">No contact person</span>}</span>
                <span data-testid="builder-email">{builder.email ? builder.email : <span className="text-zinc-400">No email</span>}</span>
                <span data-testid="builder-phone">{builder.phone ? builder.phone : <span className="text-zinc-400">No phone</span>}</span>
              </div>
            </div>
          )}
          {canManage && !builderEdit && (
            <Button size="sm" variant="outline" className="rounded-sm shrink-0" data-testid="edit-builder"
              onClick={() => { setBuilderForm({ contact_person: builder.contact_person || "", email: builder.email || "", phone: builder.phone || "" }); setBuilderEdit(true); }}>
              Edit
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      ) : projects.length === 0 ? (
        <div className="border border-zinc-200 bg-white p-12 text-center">
          <h3 className="font-heading text-xl font-bold mb-2">No projects yet</h3>
          <p className="text-zinc-500 text-sm">{canManage ? "Create your first project in this folder." : "No projects here yet."}</p>
        </div>
      ) : canManage ? (
        <Reorder.Group axis="y" values={projects} onReorder={setProjects} className="space-y-3">
          {projects.map((p) => (
            <Reorder.Item key={p.project_id} value={p} onDragEnd={persistOrder}
              className="bg-white border border-zinc-200 p-4 flex items-center gap-3 hover:border-zinc-300 transition-colors">
              <ProjectRow p={p} {...rowProps} />
            </Reorder.Item>
          ))}
        </Reorder.Group>
      ) : (
        <div className="space-y-3">
          {projects.map((p) => (
            <div key={p.project_id} className="bg-white border border-zinc-200 p-4 flex items-center gap-3">
              <ProjectRow p={p} {...rowProps} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
